import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import myAxios from '../AxiosUrl';
import styles from './SEOManagement.module.css';
import { AiOutlineSave } from 'react-icons/ai';

function SEOSettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('sitemap');

    const uid = localStorage.getItem('id');

    useEffect(() => {
        fetchSettings();
    }, []);
    const fetchSettings = async () => {
        setLoading(true);
        try {
            const response = await myAxios.get(`/seo/settings/${uid}`);
            if (response.data.success) {
                const data = response.data.data;
                
                if (data.manifest) {
                    data.manifest.name = data.manifest.name || '';
                    data.manifest.shortName = data.manifest.shortName || '';
                    data.manifest.description = data.manifest.description || '';
                }
                setSettings(data);
            }
        } catch (error) {
            console.error('Error fetching SEO settings:', error);
            // If settings don't exist yet, initialize with defaults
            if (error.response?.status === 404 || !error.response) {
                setSettings({
                    sitemap: {
                        enabled: true,
                        includeProducts: true,
                        includeCategories: true,
                        includePages: true,
                        productPriority: 0.7,
                        categoryPriority: 0.8,
                        homePagePriority: 1.0,
                        productChangeFreq: 'weekly',
                        categoryChangeFreq: 'weekly',
                        includeImages: true,
                        maxUrls: 50000,
                        excludeOutOfStock: false,
                        includeDeals: true,
                        includeStaticPages: true
                    },
                    robots: {
                        enabled: true,
                        allowAll: true,
                        disallowPaths: ['/checkout', '/checkout/*', '/cart', '/admin', '/admin/*', '/api', '/api/*'],
                        allowPaths: [],
                        customRules: '',
                        crawlDelay: 0,
                        specificBots: []
                    },
                    manifest: {
                        enabled: true,
                        name: '',
                        shortName: '',
                        description: '',
                        startUrl: '/',
                        backgroundColor: '#ffffff',
                        themeColor: '#000000',
                        display: 'standalone',
                        orientation: 'portrait',
                        categories: ['shopping', 'ecommerce']
                    }
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const response = await myAxios.put(`/seo/settings/${uid}`, settings);
            if (response.data.success) {
                toast.success('SEO settings saved successfully! Caches have been cleared.');
                fetchSettings();
            }
        } catch (error) {
            console.error('Error saving SEO settings:', error);
            toast.error(error.response?.data?.error || 'Failed to save SEO settings');
        } finally {
            setLoading(false);
        }
    };

    const updateSetting = (section, field, value) => {
        setSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className={styles.header}>
                <div>
                    <h1>SEO Configuration</h1>
                    <p>Manage Sitemap, Robots.txt, and PWA Manifest settings</p>
                </div>
                <button className={styles.addButton} onClick={handleSave} disabled={loading || !settings}>
                    <AiOutlineSave /> {loading ? 'Saving...' : 'Save Settings'}
                </button>
            </div>

            {/* Tab Navigation */}
            <div className={styles.tabContainer}>
                <div className={styles.tabButtons}>
                    <button
                        onClick={() => setActiveTab('sitemap')}
                        className={activeTab === 'sitemap' ? styles.tabActive : styles.tab}
                    >
                        Sitemap.xml
                    </button>
                    <button
                        onClick={() => setActiveTab('robots')}
                        className={activeTab === 'robots' ? styles.tabActive : styles.tab}
                    >
                        Robots.txt
                    </button>
                    <button
                        onClick={() => setActiveTab('manifest')}
                        className={activeTab === 'manifest' ? styles.tabActive : styles.tab}
                    >
                        Manifest (PWA)
                    </button>
                </div>
            </div>

            {/* Content Card */}
            <div className={styles.contentCard}>
                {!settings ? (
                    <div>
                        {/* Section Title */}
                        <div className={styles.skeleton} style={{ width: '200px', height: '20px', marginBottom: '24px' }}></div>

                        {/* Toggle Switches (Checkboxes) */}
                        {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                                <div className={styles.skeleton} style={{ width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0 }}></div>
                                <div className={styles.skeleton} style={{ width: `${120 + (item * 20) % 100}px`, height: '14px' }}></div>
                            </div>
                        ))}

                        {/* Number Input Fields */}
                        {[1, 2, 3].map((item) => (
                            <div key={item} style={{ marginBottom: '20px' }}>
                                <div className={styles.skeleton} style={{ width: `${140 + item * 20}px`, height: '14px', marginBottom: '8px' }}></div>
                                <div className={styles.skeleton} style={{ width: '100%', height: '42px', borderRadius: '6px' }}></div>
                            </div>
                        ))}

                        {/* Dropdown Field */}
                        <div style={{ marginBottom: '20px' }}>
                            <div className={styles.skeleton} style={{ width: '180px', height: '14px', marginBottom: '8px' }}></div>
                            <div className={styles.skeleton} style={{ width: '100%', height: '42px', borderRadius: '6px' }}></div>
                        </div>

                        {/* Max URLs Field */}
                        <div style={{ marginBottom: '20px' }}>
                            <div className={styles.skeleton} style={{ width: '170px', height: '14px', marginBottom: '8px' }}></div>
                            <div className={styles.skeleton} style={{ width: '100%', height: '42px', borderRadius: '6px' }}></div>
                        </div>
                    </div>
                ) : (
                <>
                {activeTab === 'sitemap' && (
                    <div>
                        <h3 className={styles.sectionTitle}>Sitemap Configuration</h3>

                        {!settings.sitemap.enabled && (
                            <div className={styles.warnNote}>
                                Your sitemap is switched off. Google and other search engines find new products and pages more slowly without one; keep it on unless you have a reason.
                            </div>
                        )}

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.sitemap.enabled}
                                    onChange={(e) => updateSetting('sitemap', 'enabled', e.target.checked)}
                                />
                                Enable Sitemap Generation
                            </label>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.sitemap.includeProducts}
                                    onChange={(e) => updateSetting('sitemap', 'includeProducts', e.target.checked)}
                                />
                                Include Products
                            </label>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.sitemap.includeCategories}
                                    onChange={(e) => updateSetting('sitemap', 'includeCategories', e.target.checked)}
                                />
                                Include Categories
                            </label>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.sitemap.includeImages}
                                    onChange={(e) => updateSetting('sitemap', 'includeImages', e.target.checked)}
                                />
                                Include Product Images
                            </label>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.sitemap.excludeOutOfStock}
                                    onChange={(e) => updateSetting('sitemap', 'excludeOutOfStock', e.target.checked)}
                                />
                                Exclude Out of Stock Products
                            </label>
                            <small>Judged from each product's stock quantity, the same as the product page</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.sitemap.includeDeals !== false}
                                    onChange={(e) => updateSetting('sitemap', 'includeDeals', e.target.checked)}
                                />
                                Include Deal Pages (Hot Deals, Trending Now, New Arrivals)
                            </label>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.sitemap.includeStaticPages !== false}
                                    onChange={(e) => updateSetting('sitemap', 'includeStaticPages', e.target.checked)}
                                />
                                Include Static Pages (About, Contact, Policies)
                            </label>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Homepage Priority (0.0 - 1.0)</label>
                            <input
                                type="number"
                                min="0"
                                max="1"
                                step="0.1"
                                value={settings.sitemap.homePagePriority}
                                onChange={(e) => updateSetting('sitemap', 'homePagePriority', parseFloat(e.target.value))}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Category Priority (0.0 - 1.0)</label>
                            <input
                                type="number"
                                min="0"
                                max="1"
                                step="0.1"
                                value={settings.sitemap.categoryPriority}
                                onChange={(e) => updateSetting('sitemap', 'categoryPriority', parseFloat(e.target.value))}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Product Priority (0.0 - 1.0)</label>
                            <input
                                type="number"
                                min="0"
                                max="1"
                                step="0.1"
                                value={settings.sitemap.productPriority}
                                onChange={(e) => updateSetting('sitemap', 'productPriority', parseFloat(e.target.value))}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Product Change Frequency</label>
                            <select
                                value={settings.sitemap.productChangeFreq}
                                onChange={(e) => updateSetting('sitemap', 'productChangeFreq', e.target.value)}
                            >
                                <option value="always">Always</option>
                                <option value="hourly">Hourly</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="yearly">Yearly</option>
                                <option value="never">Never</option>
                            </select>
                            <small>Google ignores priority and change frequency (Bing may use them). Google uses each page's last-modified date, which is filled in automatically from your product updates.</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Maximum URLs (max 50,000)</label>
                            <input
                                type="number"
                                min="1"
                                max="50000"
                                value={settings.sitemap.maxUrls}
                                onChange={(e) => updateSetting('sitemap', 'maxUrls', parseInt(e.target.value))}
                            />
                            <small>Products past this number (oldest first) are left out of the sitemap</small>
                        </div>
                    </div>
                )}

                {activeTab === 'robots' && (
                    <div>
                        <h3 className={styles.sectionTitle}>Robots.txt Configuration</h3>

                        {settings.robots.enabled && !settings.robots.allowAll && (
                            <div className={styles.warnNote}>
                                "Allow All Bots" is off: robots.txt blocks every crawler from your whole store, including Google, Bing and AI assistants, so your store will drop out of search results. Turn it back on unless you really want that.
                            </div>
                        )}

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.robots.enabled}
                                    onChange={(e) => updateSetting('robots', 'enabled', e.target.checked)}
                                />
                                Enable Robots.txt
                            </label>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.robots.allowAll}
                                    onChange={(e) => updateSetting('robots', 'allowAll', e.target.checked)}
                                />
                                Allow All Bots
                            </label>
                            <small>Search engines and AI crawlers (ChatGPT, Claude, Perplexity, Gemini, Apple, Common Crawl) may read your public pages, except the paths below. To block one crawler, add a rule under Custom Rules, e.g. "User-agent: GPTBot" then "Disallow: /".</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Disallow Paths (one per line)</label>
                            <textarea
                                rows={6}
                                value={settings.robots.disallowPaths.join('\n')}
                                onChange={(e) => updateSetting('robots', 'disallowPaths', e.target.value.split('\n').filter(p => p.trim()))}
                                placeholder="/checkout&#10;/admin&#10;/api"
                            />
                            <small>Don't block pages you want in Google. Blocking a page stops crawling but doesn't remove it from search; use a noindex robots meta tag for that.</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Crawl Delay (seconds, 0 = no delay)</label>
                            <input
                                type="number"
                                min="0"
                                value={settings.robots.crawlDelay}
                                onChange={(e) => updateSetting('robots', 'crawlDelay', parseInt(e.target.value))}
                            />
                            <small>Google ignores Crawl-delay; Bing and Yandex follow it. Keep 0 unless crawlers slow your store down.</small>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Custom Rules (optional)</label>
                            <textarea
                                rows={4}
                                value={settings.robots.customRules}
                                onChange={(e) => updateSetting('robots', 'customRules', e.target.value)}
                                placeholder="Add custom robots.txt rules here..."
                            />
                        </div>
                    </div>
                )}

                {activeTab === 'manifest' && (
                    <div>
                        <h3 className={styles.sectionTitle}>PWA Manifest Configuration</h3>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={settings.manifest.enabled}
                                    onChange={(e) => updateSetting('manifest', 'enabled', e.target.checked)}
                                />
                                Enable PWA Manifest
                            </label>
                        </div>

                        <div className={styles.formGroup}>
                            <label>App Name</label>
                            <input
                                type="text"
                                value={settings.manifest.name}
                                onChange={(e) => updateSetting('manifest', 'name', e.target.value)}
                                placeholder="My Awesome Shop"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Short Name (12 chars max)</label>
                            <input
                                type="text"
                                maxLength={12}
                                value={settings.manifest.shortName}
                                onChange={(e) => updateSetting('manifest', 'shortName', e.target.value)}
                                placeholder="MyShop"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Description</label>
                            <textarea
                                rows={3}
                                value={settings.manifest.description}
                                onChange={(e) => updateSetting('manifest', 'description', e.target.value)}
                                placeholder="Online shopping with fast delivery..."
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Theme Color (hex)</label>
                            <input
                                type="color"
                                value={settings.manifest.themeColor}
                                onChange={(e) => updateSetting('manifest', 'themeColor', e.target.value)}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Background Color (hex)</label>
                            <input
                                type="color"
                                value={settings.manifest.backgroundColor}
                                onChange={(e) => updateSetting('manifest', 'backgroundColor', e.target.value)}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Display Mode</label>
                            <select
                                value={settings.manifest.display}
                                onChange={(e) => updateSetting('manifest', 'display', e.target.value)}
                            >
                                <option value="fullscreen">Fullscreen</option>
                                <option value="standalone">Standalone</option>
                                <option value="minimal-ui">Minimal UI</option>
                                <option value="browser">Browser</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Orientation</label>
                            <select
                                value={settings.manifest.orientation}
                                onChange={(e) => updateSetting('manifest', 'orientation', e.target.value)}
                            >
                                <option value="any">Any</option>
                                <option value="portrait">Portrait</option>
                                <option value="landscape">Landscape</option>
                            </select>
                        </div>
                    </div>
                )}
                </>)}
            </div>

            {/* Info Note */}
            <div className={styles.infoNote}>
                <p>
                    <strong>Note:</strong> Changes to these settings will automatically clear the cache for sitemap.xml, robots.txt, and manifest.json.
                    The updated files will be generated on the next request.
                </p>
            </div>
        </div>
    );
}

export default SEOSettings;
